export type {
  BaseUnitInfo,
  DisplayConfig,
  DisplayNotes,
  UnitConfigParams,
} from './BaseUnitConfig';
export { BaseUnitConfig } from './BaseUnitConfig';
export type { PortConfiguration } from './PortConfig';
export { COMMON_PORT_COUNTS, getPortCount } from './PortConfig';

export enum UnitSuperType {
  FEEDERS = 'FEEDERS',
  CRUSHERS = 'CRUSHERS',
  SCREENS = 'SCREENS',
  STORAGE = 'STORAGE',
  GENERIC = 'GENERIC',
}

export enum UnitTypes {
  FEED = 'FEED',
  STORAGE = 'STORAGE',
  STORAGE_FEEDER = 'STORAGE_FEEDER',
  STORAGE_FEEDER_2_OUTPUTS = 'STORAGE_FEEDER_2_OUTPUTS',
  ADD = 'ADD',
  SPLIT = 'SPLIT',
  // Crushers
  CONE_CRUSHER_CH = 'CONE_CRUSHER_CH',
  CONE_CRUSHER_CS = 'CONE_CRUSHER_CS',
  JAW_CRUSHER_CJ = 'JAW_CRUSHER_CJ',
  IMPACT_CRUSHER_CV = 'IMPACT_CRUSHER_CV',
  IMPACT_CRUSHER_CI = 'IMPACT_CRUSHER_CI',
  GYRATORY_CRUSHER_CG = 'GYRATORY_CRUSHER_CG',
  HYBRID_CRUSHER_CR = 'HYBRID_CRUSHER_CR',
  // Screen
  LINEAR_MOTION_SCREEN = 'LINEAR_MOTION_SCREEN',
  MULTISLOPE_SCREEN = 'MULTISLOPE_SCREEN',
  BANANA_SCREEN = 'BANANA_SCREEN',
  SCHENCK_SCREEN = 'SCHENCK_SCREEN',
  KWATANI_SCREEN = 'KWATANI_SCREEN',
  FREEFALL_SCREEN_SF = 'FREEFALL_SCREEN_SF',
  FREEFALL_SCREEN_SS = 'FREEFALL_SCREEN_SS',
  GRIZZLY_SCREEN = 'GRIZZLY_SCREEN',
  // Feeders
  PAN_FEEDER_SP = 'PAN_FEEDER_SP',
  PLATE_FEEDER_SH = 'PLATE_FEEDER_SH',
  GRIZZLY_FEEDER_GENERAL = 'GRIZZLY_FEEDER',
  GRIZZLY_FEEDER_SW = 'GRIZZLY_FEEDER_SW',
  GRIZZLY_FEEDER_SV = 'GRIZZLY_FEEDER_SV',
  GRIZZLY_FEEDER_SV_E = 'GRIZZLY_FEEDER_SV_E',
  FEEDER_AND_SCREEN = 'FEEDER_FEEDER_SCREEN_ST',
  // Mobile
  MOBILE_TRACKED_CONE = 'MOBILE_TRACKED_CONE',
  MOBILE_WHEELED_CONE = 'MOBILE_WHEELED_CONE',
  MOBILE_TRACKED_JAW = 'MOBILE_TRACKED_JAW',
  MOBILE_TRACKED_SCREEN = 'MOBILE_TRACKED_SCREEN',
  MOBILE_WHEELED_SCREEN = 'MOBILE_WHEELED_SCREEN',
  MOBILE_TRACKED_HSI = 'MOBILE_TRACKED_HSI',
  MOBILE_WHEELED_HSI = 'MOBILE_WHEELED_HSI',
  MOBILE_VSI = 'MOBILE_VSI',
  MOBILE_JAW_AND_CONE = 'MOBILE_JAW_AND_CONE',
  MOBILE_WHEELED_JAW = 'MOBILE_WHEELED_JAW',
  // COMMON
  MOBILE_JAW_COMMON = 'MOBILE_JAW',
  MOBILE_CONE_COMMON = 'MOBILE_CONE',
  MOBILE_SCREEN_COMMON = 'MOBILE_SCREEN',
  MOBILE_HSI_COMMON = 'MOBILE_HSI',
  CONVENTIONAL_SCREEN = 'CONVENTIONAL_SCREEN',
  // AttachmentTool
  BREAKER = 'BREAKER',
  // OTHER
  PRINT_WINDOW = 'PRINT_WINDOW',
  FREE_TEXT_BOX = 'FREE_TEXT_BOX',
  HIGHLIGHT_TEXT_BOX = 'HIGHLIGHT_TEXT_BOX',
  //Generic
  GENERIC_CRUSHER = 'GENERIC_CRUSHER',
  GENERIC_SCREEN = 'GENERIC_SCREEN',
}

export const isUnitType = (unitType: unknown): unitType is UnitTypes =>
  (Object.values(UnitTypes) as unknown[]).includes(unitType);

export type AllUnits = Exclude<
  UnitTypes,
  | UnitTypes.PRINT_WINDOW
  | UnitTypes.GRIZZLY_FEEDER_GENERAL
  | UnitTypes.MOBILE_JAW_COMMON
  | UnitTypes.MOBILE_CONE_COMMON
  | UnitTypes.MOBILE_SCREEN_COMMON
  | UnitTypes.MOBILE_HSI_COMMON
  | UnitTypes.HIGHLIGHT_TEXT_BOX
>;

export const feedUnitTypes = [UnitTypes.FEED];

export const crusherUnitTypes = [
  UnitTypes.JAW_CRUSHER_CJ,
  UnitTypes.CONE_CRUSHER_CS,
  UnitTypes.CONE_CRUSHER_CH,
  UnitTypes.IMPACT_CRUSHER_CV,
  UnitTypes.IMPACT_CRUSHER_CI,
  UnitTypes.HYBRID_CRUSHER_CR,
  UnitTypes.GYRATORY_CRUSHER_CG,
];

export const screenUnitTypes = [
  UnitTypes.CONVENTIONAL_SCREEN,
  UnitTypes.LINEAR_MOTION_SCREEN,
  UnitTypes.MULTISLOPE_SCREEN,
  UnitTypes.BANANA_SCREEN,
  UnitTypes.SCHENCK_SCREEN,
  UnitTypes.KWATANI_SCREEN,
  UnitTypes.FREEFALL_SCREEN_SF,
  UnitTypes.FREEFALL_SCREEN_SS,
  UnitTypes.GRIZZLY_SCREEN,
];

export const genericUnitTypes = [
  UnitTypes.GENERIC_CRUSHER,
  UnitTypes.GENERIC_SCREEN,
];

export const feederUnitTypes = [
  UnitTypes.PAN_FEEDER_SP,
  UnitTypes.PLATE_FEEDER_SH,
  UnitTypes.GRIZZLY_FEEDER_GENERAL,
  UnitTypes.GRIZZLY_FEEDER_SW,
  UnitTypes.GRIZZLY_FEEDER_SV,
  UnitTypes.GRIZZLY_FEEDER_SV_E,
  UnitTypes.FEEDER_AND_SCREEN,
];

export const mobileTrackedUnitTypes = [
  UnitTypes.MOBILE_TRACKED_JAW,
  UnitTypes.MOBILE_TRACKED_CONE,
  UnitTypes.MOBILE_TRACKED_HSI,
  UnitTypes.MOBILE_TRACKED_SCREEN,
];

export const mobileWheeledUnitTypes = [
  UnitTypes.MOBILE_WHEELED_JAW,
  UnitTypes.MOBILE_JAW_AND_CONE,
  UnitTypes.MOBILE_WHEELED_CONE,
  UnitTypes.MOBILE_WHEELED_HSI,
  UnitTypes.MOBILE_VSI,
  UnitTypes.MOBILE_WHEELED_SCREEN,
];

export const storageUnitTypes = [
  UnitTypes.STORAGE,
  UnitTypes.STORAGE_FEEDER,
  UnitTypes.STORAGE_FEEDER_2_OUTPUTS,
];

/**
 * Unit types that support risk assessment features.
 * Includes all non-generic crushers and screens only.
 * Excludes: generic units (GENERIC_CRUSHER, GENERIC_SCREEN), feeders, and other unit types.
 *
 * @deprecated Use RISK_ASSESSMENT_TYPES and supportsRiskAssessment() from util/unitTypeClassification instead
 */
export const riskAssessmentSupportedUnitTypes = [
  ...crusherUnitTypes,
  ...screenUnitTypes,
];

export const feedUnitNames = [
  { type: UnitTypes.FEED, name: 'Truck', variantId: 1 },
  { type: UnitTypes.FEED, name: 'Excavator', variantId: 2 },
  { type: UnitTypes.FEED, name: 'Wheel loader', variantId: 3 },
  { type: UnitTypes.FEED, name: 'Generic feed', variantId: 4 },
];

export const storageUnitNames = [
  { type: UnitTypes.STORAGE, name: 'Storage', variantId: 1 },
  {
    type: UnitTypes.STORAGE_FEEDER,
    name: 'Storage with one output',
    variantId: 3,
  },
  {
    type: UnitTypes.STORAGE_FEEDER_2_OUTPUTS,
    name: 'Storage with two outputs',
    variantId: 2,
  },
];

export const addUnitNames = [
  { type: UnitTypes.ADD, name: '2 inputs', variantId: 2 },
  { type: UnitTypes.ADD, name: '3 inputs', variantId: 3 },
  { type: UnitTypes.ADD, name: '4 inputs', variantId: 4 },
];

export const splitUnitNames = [
  { type: UnitTypes.SPLIT, name: '2 outputs', variantId: 2 },
  { type: UnitTypes.SPLIT, name: '3 outputs', variantId: 3 },
];

export const crusherUnitNames = [
  {
    type: UnitTypes.CONE_CRUSHER_CH,
    name: 'Cone crusher (CH)',
    name2: 'Cone crusher',
  },
  {
    type: UnitTypes.CONE_CRUSHER_CS,
    name: 'Cone crusher (CS)',
    name2: 'Cone crusher',
  },
  {
    type: UnitTypes.IMPACT_CRUSHER_CI,
    name: 'HSI crusher (CI)',
    name2: 'HSI crusher',
  },
  {
    type: UnitTypes.GYRATORY_CRUSHER_CG,
    name: 'Gyratory crusher (CG)',
    name2: 'Gyratory crusher',
  },
  {
    type: UnitTypes.IMPACT_CRUSHER_CV,
    name: 'VSI crusher (CV)',
    name2: 'VSI crusher',
  },
  {
    type: UnitTypes.HYBRID_CRUSHER_CR,
    name: 'Hybrid crusher (CR)',
    name2: 'Hybrid crusher',
  },
  {
    type: UnitTypes.JAW_CRUSHER_CJ,
    name: 'Jaw crusher (CJ)',
    name2: 'Jaw crusher',
  },
];

export const genericUnitNames = [
  {
    type: UnitTypes.GENERIC_CRUSHER,
    name: 'Crusher/Mill',
    name2: 'Generic equipment',
  },
  {
    type: UnitTypes.GENERIC_SCREEN,
    name: 'Screen',
    name2: 'Generic equipment',
  },
];

export const screenUnitNames = [
  {
    type: UnitTypes.CONVENTIONAL_SCREEN,
    name: 'Circular motion screen (SJ/SA)',
    name2: 'Circular motion screen',
  },
  {
    type: UnitTypes.LINEAR_MOTION_SCREEN,
    name: 'Linear motion screen (SL)',
    name2: 'Linear motion screen',
  },
  {
    type: UnitTypes.MULTISLOPE_SCREEN,
    name: 'Multislope screen (SM)',
    name2: 'Multislope screen',
  },
  {
    type: UnitTypes.FREEFALL_SCREEN_SF,
    name: 'Free fall screen (SF)',
    name2: 'Free fall screen',
  },
  {
    type: UnitTypes.FREEFALL_SCREEN_SS,
    name: 'Free fall screen (SS)',
    name2: 'Free fall screen',
  },
  {
    type: UnitTypes.GRIZZLY_SCREEN,
    name: 'Grizzly screen (SG)',
    name2: 'Grizzly screen',
  },
  { type: UnitTypes.BANANA_SCREEN, name: 'Multislope (generic)' },
  { type: UnitTypes.SCHENCK_SCREEN, name: 'Sandvik/Schenck screen' },
  {
    type: UnitTypes.KWATANI_SCREEN,
    name: 'Multislope (KM)',
    name2: 'Multislope screen',
  },
];

export const feederUnitNames = [
  {
    type: UnitTypes.FEEDER_AND_SCREEN,
    name: 'Feeder and screen (ST)',
    name2: 'Feeder and screen',
  },
  {
    type: UnitTypes.PAN_FEEDER_SP,
    name: 'Pan feeder (SP)',
    name2: 'Pan feeder',
  },
  {
    type: UnitTypes.PLATE_FEEDER_SH,
    name: 'Plate feeder (SH)',
    name2: 'Plate feeder',
  },
  {
    type: UnitTypes.GRIZZLY_FEEDER_SW,
    name: 'Grizzly feeder (SW)',
    name2: 'Grizzly feeder',
  },
  {
    type: UnitTypes.GRIZZLY_FEEDER_SV,
    name: 'Grizzly feeder (SV)',
    name2: 'Grizzly feeder',
  },
  {
    type: UnitTypes.GRIZZLY_FEEDER_SV_E,
    name: 'Grizzly feeder (SV-E)',
    name2: 'Grizzly feeder',
  },
];

export const mobileTrackedUnitNames = [
  { type: UnitTypes.MOBILE_TRACKED_CONE, name: 'Cone', name2: 'Tracked cone' },
  { type: UnitTypes.MOBILE_TRACKED_JAW, name: 'Jaw', name2: 'Tracked jaw' },
  {
    type: UnitTypes.MOBILE_TRACKED_SCREEN,
    name: 'Screen',
    name2: 'Tracked screen',
  },
  { type: UnitTypes.MOBILE_TRACKED_HSI, name: 'HSI', name2: 'Tracked HSI' },
];

export const mobileWheeledUnitNames = [
  { type: UnitTypes.MOBILE_WHEELED_HSI, name: 'HSI', name2: 'Wheeled HSI' },
  { type: UnitTypes.MOBILE_WHEELED_CONE, name: 'Cone', name2: 'Wheeled cone' },
  { type: UnitTypes.MOBILE_WHEELED_JAW, name: 'Jaw', name2: 'Wheeled jaw' },
  {
    type: UnitTypes.MOBILE_JAW_AND_CONE,
    name: 'Jaw and cone',
    name2: 'Wheeled jaw and cone',
  },
  { type: UnitTypes.MOBILE_VSI, name: 'VSI', name2: 'Wheeled VSI' },
  {
    type: UnitTypes.MOBILE_WHEELED_SCREEN,
    name: 'Screen',
    name2: 'Wheeled screen',
  },
];

export type UnitName = {
  type: UnitTypes;
  name: string;
  name2?: string;
  variantId?: VariantId;
};

export type VariantId = number;

export const UnitNames: UnitName[] = [
  ...feedUnitNames,
  ...storageUnitNames,
  ...crusherUnitNames,
  ...screenUnitNames,
  ...genericUnitNames,
  ...addUnitNames,
  ...splitUnitNames,
  ...feederUnitNames,
  { type: UnitTypes.FREE_TEXT_BOX, name: 'Text box' },
  { type: UnitTypes.BREAKER, name: 'Breaker' },
  ...mobileTrackedUnitNames,
  ...mobileWheeledUnitNames,
  { type: UnitTypes.PRINT_WINDOW, name: 'Printwindow' },
];
